import {api} from 'app/services/api';
import {catchDataError, parseLinkHeader} from 'app/helpers/apiHelpers';
import {convertDateToParam} from 'app/helpers/actionsHelpers';
import {updateHistory} from 'app/actions/historyActions';
import {updateHttpErrorSubroute} from 'app/components/error/ErrorHandlerActions';
import {fetchNextApiCall} from 'app/services/fetchNextApiCall';
import {Dispatch} from 'react';
import {AxiosResponse} from 'axios';
import {DistrictMatchesResponse} from 'app/components/matchCalendar/DistrictMatchesResponse';
import MatchCalendarDefaultPageState from 'app/components/matchCalendar/MatchCalendarDefaultPageState';
import DistrictMatchesResponseData from 'app/components/matchCalendar/DistrictMatchesResponseData';

const getMatchesByDistrict = (
  district: string,
  date?: string,
  filter?: string,
  controller?: AbortController
): Promise<AxiosResponse<DistrictMatchesResponse>> => {
  if (!date) {
    date = convertDateToParam();
  }
  const filterQuery = filter ? `&filter=${filter}` : '';
  return api.get(`/districts/${district}/matches?date=${date}${filterQuery}`, {signal: controller?.signal});
};

function updateMatchCalendarDefault(data: {date?: string; filter?: string}) {
  return {
    type: 'MATCHCALENDAR_DEFAULT_UPDATE',
    data,
  };
}

function updateDefaultDistrictMatches(data: {items: DistrictMatchesResponse; nextUrl: string | null}) {
  return {
    type: 'MATCHCALENDAR_DEFAULT_DISTRICT_UPDATE',
    data,
  };
}

function updateDefaultProMatches(data: {items: DistrictMatchesResponse; nextUrl: string | null}) {
  return {
    type: 'MATCHCALENDAR_DEFAULT_PRO_UPDATE',
    data,
  };
}

function fetchingDefaultDistrictMatches() {
  return {
    type: 'MATCHCALENDAR_DEFAULT_DISTRICT_FETCHING',
  };
}

function fetchingDefaultProMatches() {
  return {
    type: 'MATCHCALENDAR_DEFAULT_PRO_FETCHING',
  };
}

function errorDefaultDistrictMatches(data: {status: number; message: string}) {
  return {
    type: 'MATCHCALENDAR_DEFAULT_DISTRICT_ERROR',
    data,
  };
}

function errorDefaultProMatches(data: {status: number; message: string}) {
  return {
    type: 'MATCHCALENDAR_DEFAULT_PRO_ERROR',
    data,
  };
}

function overwriteMatchCalendarMatches(data: MatchCalendarDefaultPageState) {
  return {
    type: 'MATCHCALENDAR_DEFAULT_OVERWRITE',
    data,
  };
}

type Action = {type: string; data?: any};

const fetchMatchesByDistrict = (
  slug: string,
  dispatchRedux: Dispatch<Action>,
  date?: string,
  filter?: string,
  abortController?: AbortController
) => {
  return function (dispatch: Dispatch<Action>, state: MatchCalendarDefaultPageState) {
    const {date: oldDate, filter: oldFilter, districtMatches, proMatches} = state;

    const hasSameMeta = date === oldDate && filter === oldFilter;
    const hasLoadedDistrictMatches = districtMatches.items.length > 0 || !districtMatches.isFetching;
    const hasLoadedProMatches = proMatches.items.length > 0 || !proMatches.isFetching;

    if (hasSameMeta && (hasLoadedDistrictMatches || hasLoadedProMatches)) {
      return;
    }

    dispatch(updateMatchCalendarDefault({date, filter}));
    dispatch(fetchingDefaultDistrictMatches());

    const districtMatchesPromise = fetchDistrictMatchesCsr(
      slug,
      dispatch,
      dispatchRedux,
      date,
      filter,
      abortController
    );
    const proMatchesPromise = fetchProMatchesCsr(slug, dispatch, date, filter, abortController);

    dispatch(fetchingDefaultDistrictMatches());
    return Promise.all([districtMatchesPromise, proMatchesPromise]);
  };
};

const infinitelyFetchMatchesByDistrict = () => {
  return async function (dispatch: Dispatch<Action>, state: MatchCalendarDefaultPageState) {
    const {districtMatches, proMatches} = state;

    if (districtMatches.isFetching || proMatches.isFetching) {
      return;
    }

    if (districtMatches.nextUrl) {
      dispatch(fetchingDefaultDistrictMatches());
      try {
        const response = await fetchNextApiCall<DistrictMatchesResponse>(api, districtMatches.nextUrl);
        const meta = parseLinkHeader(response.headers.link);
        const data = {items: response.data, nextUrl: meta.next || null, error: null, isFetching: false};
        dispatch(updateDefaultDistrictMatches(data));
      } catch (e) {
        const errorData = catchDataError(e);
        dispatch(errorDefaultDistrictMatches(errorData));
      }
      return;
    }

    if (proMatches.nextUrl) {
      try {
        dispatch(fetchingDefaultProMatches());
        const response = await fetchNextApiCall<DistrictMatchesResponse>(api, proMatches.nextUrl);
        const meta = parseLinkHeader(response.headers.link);
        const data = {items: response.data, nextUrl: meta.next || null, error: null, isFetching: false};
        dispatch(updateDefaultProMatches(data));
      } catch (e) {
        const errorData = catchDataError(e);
        dispatch(errorDefaultProMatches(errorData));
      }
      return;
    }
  };
};

function fetchMatchCalendarDefaultSSR(slug: string, date?: string, filter?: string) {
  return async function (dispatch: Dispatch<Action>) {
    const districtMatchesPromise = fetchDistrictMatches(slug, date, filter);

    const proMatchesPromise = fetchProMatchesSsr(slug, date, filter);

    try {
      const [districtMatches, proMatches] = await Promise.all([districtMatchesPromise, proMatchesPromise]);

      dispatch(
        updateHistory('undefined', {
          store: 'MatchCalendarDefaultPage',
          data: {date, filter, districtMatches, proMatches},
        })
      );
    } catch (e) {
      const errorData = catchDataError(e);
      dispatch(updateHttpErrorSubroute(errorData));
    }
  };
}

const fetchDistrictMatches = async (
  slug: string,
  date?: string,
  filter?: string,
  abortController?: AbortController
): Promise<DistrictMatchesResponseData> => {
  const response = await getMatchesByDistrict(slug, date, filter, abortController);
  const meta = parseLinkHeader(response.headers.link);
  return {items: response.data, nextUrl: meta.next ? meta.next : null, error: null, isFetching: false};
};

const fetchDistrictMatchesCsr = async (
  slug: string,
  dispatch: Dispatch<Action>,
  dispatchRedux: Dispatch<Action>,
  date?: string,
  filter?: string,
  abortController?: AbortController
): Promise<void> => {
  try {
    const data = await fetchDistrictMatches(slug, date, filter, abortController);
    dispatch(updateDefaultDistrictMatches(data));
  } catch (e) {
    const errorData = catchDataError(e);
    dispatchRedux(updateHttpErrorSubroute(errorData));
  }
};

const fetchProMatchesCsr = async (
  slug: string,
  dispatch: Dispatch<Action>,
  date?: string,
  filter?: string,
  abortController?: AbortController
): Promise<void> => {
  try {
    const data = await fetchProMatches(slug, date, filter, abortController);
    dispatch(updateDefaultProMatches(data));
  } catch (e) {
    const errorData = catchDataError(e);
    dispatch(errorDefaultProMatches(errorData));
  }
};

const fetchProMatchesSsr = (slug: string, date?: string, filter?: string): Promise<DistrictMatchesResponseData> => {
  if (['profis', 'internationaler-fussball'].includes(slug)) {
    return Promise.resolve({isFetching: false, items: [], nextUrl: null, error: null});
  }
  try {
    return fetchProMatches(slug, date, filter);
  } catch (error) {
    return Promise.resolve({error: catchDataError(error), nextUrl: null, isFetching: false, items: []});
  }
};

const fetchProMatches = async (
  slug: string,
  date?: string,
  filter?: string,
  abortController?: AbortController
): Promise<DistrictMatchesResponseData> => {
  if (['profis', 'internationaler-fussball'].includes(slug)) {
    return Promise.resolve({isFetching: false, items: [], nextUrl: null, error: null});
  }
  const response = await getMatchesByDistrict('profis', date, filter, abortController);
  const meta = parseLinkHeader(response.headers.link);
  return {items: response.data, nextUrl: meta.next || null, error: null, isFetching: false};
};

export {
  fetchMatchesByDistrict,
  infinitelyFetchMatchesByDistrict,
  fetchMatchCalendarDefaultSSR,
  overwriteMatchCalendarMatches,
};
